import { Family, Tier, STARTING_FAMILIES, FAMILY_UNLOCK_TIERS, MAX_ACTIVE_FAMILIES_PER_RUN } from '../config';

/**
 * Tracks which element families are unlocked across runs (meta progression)
 * AND which subset of those families is currently in play this run (draft).
 *
 * Why two sets: a 6×6 board cannot mathematically juggle 8 families × 7 tiers
 * without producing unmergeable orphans. Each run draws from a capped pool
 * (review fix #2), so the player drafts up to MAX_ACTIVE_FAMILIES_PER_RUN
 * families at the start of every run.
 */
export class FamilyProgressionManager {
  private unlockedFamilies: Set<Family>;
  private activeFamilies: Set<Family>;

  constructor() {
    this.unlockedFamilies = new Set(STARTING_FAMILIES);
    // By default the active set mirrors the unlocked set; the GameScene draft
    // overlay narrows it once the player has more than MAX_ACTIVE_FAMILIES_PER_RUN.
    this.activeFamilies = new Set(STARTING_FAMILIES);
  }

  /** Get all families ever unlocked across runs. */
  getUnlockedFamilies(): Family[] {
    return [...this.unlockedFamilies];
  }

  /** Get the families currently in play this run. */
  getActiveFamilies(): Family[] {
    return [...this.activeFamilies];
  }

  /** Check if a family is unlocked at all (meta progression). */
  isUnlocked(family: Family): boolean {
    return this.unlockedFamilies.has(family);
  }

  /** Check if a family is in the active draft for the current run. */
  isActive(family: Family): boolean {
    return this.activeFamilies.has(family);
  }

  /**
   * Set the draft for a new run. Silently filters to unlocked families and
   * caps at MAX_ACTIVE_FAMILIES_PER_RUN. If no valid families remain it falls
   * back to all unlocked (so a run can never be impossible).
   */
  setActiveFamilies(families: Family[]): void {
    const valid = families.filter(f => this.unlockedFamilies.has(f));
    const capped = valid.slice(0, MAX_ACTIVE_FAMILIES_PER_RUN);
    if (capped.length === 0) {
      this.activeFamilies = new Set(this.unlockedFamilies);
      return;
    }
    this.activeFamilies = new Set(capped);
  }

  /**
   * Convenience: are there enough unlocked families that the player should
   * be shown the pre-run draft screen?
   */
  needsDraft(): boolean {
    return this.unlockedFamilies.size > MAX_ACTIVE_FAMILIES_PER_RUN;
  }

  /**
   * Check if reaching a tier unlocks new families.
   * Returns the list of newly unlocked families (empty if none).
   * Newly unlocked families are NOT auto-added to the active draft — the
   * player must consciously include them on their next run.
   */
  checkTierUnlocks(tier: Tier): Family[] {
    const newlyUnlocked: Family[] = [];
    for (const entry of FAMILY_UNLOCK_TIERS) {
      if (entry.tier === tier) {
        for (const family of entry.families) {
          if (!this.unlockedFamilies.has(family)) {
            this.unlockedFamilies.add(family);
            newlyUnlocked.push(family);
          }
        }
      }
    }
    return newlyUnlocked;
  }

  /** Export state for saving. */
  exportState(): { unlockedFamilies: string[]; activeFamilies: string[] } {
    return {
      unlockedFamilies: [...this.unlockedFamilies],
      activeFamilies: [...this.activeFamilies],
    };
  }

  /** Load state from save data. */
  loadState(state: { unlockedFamilies: string[]; activeFamilies?: string[] }): void {
    this.unlockedFamilies = new Set(state.unlockedFamilies as Family[]);
    if (state.activeFamilies && state.activeFamilies.length > 0) {
      this.activeFamilies = new Set(state.activeFamilies as Family[]);
    } else {
      // Older saves: default the draft to whatever is unlocked, capped.
      const all = [...this.unlockedFamilies];
      this.activeFamilies = new Set(all.slice(0, MAX_ACTIVE_FAMILIES_PER_RUN));
    }
  }
}
